import { FlowConfig } from '@/helpers/split/splitFormFlow';
import { createContext, Dispatch, useContext } from 'react';

export const FormContext =
	// Using any's here because the context is a placeholder and it is more strictly defined when implemented.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	createContext<FormContext_Value<any, any, any>>({
		submit: () => console.warn('Submit function not implemented'),
		registerStep: () => console.warn('Register step function not implemented'),
		// Placeholder or minimal default state
		state: {},
		dispatch: () => console.warn('Dispatch function not implemented'),
		graph: {},
	});

export const useFormContext = <Steps extends string, State, Action>() =>
	useContext<FormContext_Value<Steps, State, Action>>(FormContext);

export interface FormContext_Value<Steps extends string, State, Action> {
	submit: (state: State, dispatch: Dispatch<Action>) => void;
	registerStep: (
		name: Steps,
		onNext?: UpdateActiveStep,
		onBack?: UpdateActiveStep,
	) => void;
	state: State;
	dispatch: Dispatch<Action>;
	graph: Beacon<Steps>;
}

export type UpdateActiveStep = <State, Steps>(
	stepName: Steps,
	state: State,
) => Steps;

export interface FormContext_State<Steps extends string> {
	activeStep: Steps;
	currentStep: number;
	experience: string;
	maxSteps: number;
	steps: FormContext_Step<Steps>[];
	breadcrumbs: Steps[];
	valid: boolean;
	graph: Beacon<Steps>;
	splitConfig?: FlowConfig<Steps>;
	requestStatus: 'IDLE' | 'IN_PROGRESS' | 'REJECTED' | 'SUCCESSFUL';
	showValidationErrors: boolean;
}

export interface FormContext_Step<Steps> {
	name: Steps;
	onNext?: UpdateActiveStep;
	onBack?: UpdateActiveStep;
}

export type Reducer<State, Action> = (state: State, action: Action) => State;

/**
 * Actions, first we have an enum of possible actions,
 * then that enum is mapped to the type & payload
 * That mapping is this type.
 */
export const FormContext_Actions = {
	REGISTER_STEP: 'REGISTER_STEP',
	NEXT: 'NEXT',
	BACK: 'BACK',
	VALIDATE: 'VALIDATE',
	SHOW_VALIDATION_ERRORS: 'SHOW_VALIDATION_ERRORS',
	// Submission states
	SET_IDLE: 'SET_IDLE',
	SET_REJECTED: 'SET_REJECTED',
	SET_SUCCESSFUL: 'SET_SUCCESSFUL',
	SET_IN_PROGRESS: 'SET_IN_PROGRESS',
	SET_FIELD_ERRORS: 'SET_FIELD_ERRORS',
} as const;

export type FormContext_Action = keyof typeof FormContext_Actions;

/**
 * ### Base Form Context Action Payload
 * This is the base shape of the action payload for the form context that is used
 * everywhere. These actions and the payloads are used in all multi-step forms.
 */
export interface FormContext_Action_Payload<Steps> {
	[FormContext_Actions.REGISTER_STEP]: FormContext_Step<Steps>;
	[FormContext_Actions.NEXT]: Steps;
	[FormContext_Actions.BACK]: null;
	[FormContext_Actions.VALIDATE]: boolean;
	[FormContext_Actions.SHOW_VALIDATION_ERRORS]: boolean;
	// Submission states
	[FormContext_Actions.SET_IDLE]: null;
	[FormContext_Actions.SET_REJECTED]: null;
	[FormContext_Actions.SET_SUCCESSFUL]: null;
	[FormContext_Actions.SET_IN_PROGRESS]: null;
	[FormContext_Actions.SET_FIELD_ERRORS]: Record<string, string>;
}

/*******************************************************************************
 * ActionMap
 * This generic type maps action payload shapes to action types so that we can
 * use the action type to infer the payload shape.
 *
 * @example
 * const Actions = {
 * 	ACTION: 'ACTION',
 * 	OTHER_ACTION: 'OTHER_ACTION',
 * } as const;
 *
 * interface ActionPayload {
 * 	[ActionTypes.ACTION]: string;
 * 	[ActionTypes.OTHER_ACTION]: number;
 * }
 *
 * type ActionMap = ActionMap<ActionPayload>[keyof ActionMap<ActionPayload>];
 *
 * const reducer = (state: State, action: ActionMap) => {
 * 	switch (action.type) {
 * 		case ActionTypes.ACTION:
 * 			return {
 * 				...state,
 * 				field: action.payload, // action.payload is a string
 * 			};
 * 		case ActionTypes.OTHER_ACTION:
 * 			return {
 * 				...state,
 * 				otherField: action.payload, // action.payload is a number
 * 			};
 * 		default:
 * 			return state;
 * 	}
 * };
 */
export type ActionMap<Payload extends { [index in keyof Payload]: unknown }> = {
	[Key in keyof Payload]: { type: Key; payload: Payload[Key] };
};

export type FormContext_Action_PayloadMap<Steps> = ActionMap<
	FormContext_Action_Payload<Steps>
>[keyof ActionMap<FormContext_Action_Payload<Steps>>];

export const isFormContext_Action_PayloadMap = <Steps,>(
	action: unknown,
): action is FormContext_Action_PayloadMap<Steps> => {
	const isObject = typeof action === 'object' && action !== null;
	if (!isObject) return false;
	const hasType = 'type' in action;
	if (!hasType) return false;
	const type = action.type;
	const isString = typeof type === 'string';
	if (!isString) return false;
	const isValidAction = type in FormContext_Actions;
	return isValidAction;
};

export const formContext_reducer = <
	Steps extends string,
	State extends FormContext_State<Steps>,
>(
	state: State,
	action: FormContext_Action_PayloadMap<Steps>,
): State => {
	switch (action.type) {
		case FormContext_Actions.REGISTER_STEP:
			return {
				...state,
				steps: [
					...state.steps.filter((step) => step.name !== action.payload.name),
					action.payload,
				],
				maxSteps: countSteps(state),
			};
		case FormContext_Actions.NEXT:
			if (!state.valid) return state;
			return {
				...state,
				valid: false,
				currentStep: state.currentStep + 1,
				activeStep: action.payload,
				breadcrumbs: [...state.breadcrumbs, state.activeStep],
				maxSteps: countSteps({
					...state,
					currentStep: state.currentStep + 1,
					activeStep: action.payload,
					breadcrumbs: [...state.breadcrumbs, state.activeStep],
				}),
			};
		case FormContext_Actions.BACK:
			if (state.currentStep === 0) {
				window.history.back();
				return state;
			}
			return {
				...state,
				currentStep: state.currentStep - 1,
				activeStep: state.breadcrumbs.at(-1) as Steps,
				breadcrumbs: state.breadcrumbs.slice(0, state.breadcrumbs.length - 1),
				maxSteps: countSteps({
					...state,
					currentStep: state.currentStep - 1,
					activeStep: state.breadcrumbs.at(-1) as Steps,
					breadcrumbs: state.breadcrumbs.slice(0, state.breadcrumbs.length - 1),
				}),
			};
		case FormContext_Actions.VALIDATE:
			return {
				...state,
				valid: action.payload,
				showValidationErrors: !action.payload,
			};
		case FormContext_Actions.SHOW_VALIDATION_ERRORS:
			return {
				...state,
				showValidationErrors: action.payload,
			};
		// Submission states
		case FormContext_Actions.SET_REJECTED:
			return {
				...state,
				requestStatus: 'REJECTED',
			};
		case FormContext_Actions.SET_SUCCESSFUL:
			return {
				...state,
				requestStatus: 'SUCCESSFUL',
			};
		case FormContext_Actions.SET_IN_PROGRESS:
			return {
				...state,
				requestStatus: 'IN_PROGRESS',
			};
		case FormContext_Actions.SET_IDLE:
			return {
				...state,
				requestStatus: 'IDLE',
			};
		case FormContext_Actions.SET_FIELD_ERRORS:
			return {
				...state,
				fieldErrors: action.payload,
			};
		default:
			return state;
	}
};

/**
 * Ensure state, satifies the FormContext_State interface
 */
export const satifiesFormContext_State = <Steps extends string>(
	state: unknown,
): state is FormContext_State<Steps> => {
	if (typeof state !== 'object' || state === null) return false;
	if (!('activeStep' in state)) return false;
	if (!('currentStep' in state)) return false;
	if (!('valid' in state)) return false;
	if (!('steps' in state)) return false;
	if (!('maxSteps' in state)) return false;
	if (!('graph' in state)) return false;
	return true;
};

/*******************************************************************************
 * Step Counter
 */
export const countSteps = <State, Steps extends string>(
	state: State,
): number => {
	if (!satifiesFormContext_State<Steps>(state)) return 0;
	if (!state.steps || !state.steps.length) return 0;
	const { graph } = state;
	const longestPath = findLongestPath(state.activeStep, graph);
	const count = state.currentStep + longestPath;

	return count;
};

export const findLongestPath = <Steps extends string>(
	location: Steps,
	graph: Beacon<Steps>,
) => {
	let maxPathLength = 1;

	const dfs = (node: Steps, pathLength: number) => {
		const children = graph[node];
		if (!children || children.length === 0) {
			maxPathLength = Math.max(maxPathLength, pathLength);
			return;
		}

		// Visit all children
		for (const child of children) {
			dfs(child, pathLength + 1);
		}
	};

	dfs(location, maxPathLength);
	return maxPathLength;
};

export type Beacon<Steps extends string> = { [K in Steps]?: Steps[] };
